import type {
  ClassifyResult,
  ErrorTagsRecord,
  SupabaseRateLimitExtras,
  SupabaseTagExtras,
} from "@/types";
import {
  AUTH_ERROR_REPORTING_LEVEL_MAP,
  getHttpStatusFromSupabase,
  isFunctionsHttpError,
  isPostgrestError,
  isSupabaseAuthError,
} from "../supabase";
import {
  buildErrorTagsBase,
  composeRateLimitPrefix,
  DROP_4XX,
  getHttpStatus,
  isOfflineOrNetwork,
} from "./common";

// Build standard error tags for Supabase operations (rpc/select/etc.)
export function buildSupabaseErrorTags(
  scope: "supabase",
  verdict: ClassifyResult,
  extras?: SupabaseTagExtras,
): ErrorTagsRecord {
  return buildErrorTagsBase("supabase", scope, verdict, extras);
}

// Build Supabase rate-limit key (subject by rpc/table/op)
export function buildSupabaseRateLimitKey(
  verdict: ClassifyResult,
  error: unknown,
  extras?: SupabaseRateLimitExtras,
): string {
  if (isOfflineOrNetwork(error)) return "offline";
  const prefix = composeRateLimitPrefix(verdict, error);
  const subject = extras?.rpc ?? extras?.table ?? extras?.op ?? "";
  return `sb:${prefix}:${subject}`;
}

// Classify Supabase-originated errors into reporting levels and optional status
export function classifySupabaseError(error: unknown): ClassifyResult {
  // AuthApiError → decision by code, fallback by status
  if (isSupabaseAuthError(error)) {
    const code =
      typeof (error as { code?: unknown }).code === "string"
        ? (error as { code: string }).code
        : undefined;
    const mapped = code ? AUTH_ERROR_REPORTING_LEVEL_MAP[code] : undefined;
    if (mapped)
      return { level: mapped, status: getHttpStatusFromSupabase(error) };

    const s = getHttpStatusFromSupabase(error);
    if (s !== undefined) {
      if (s >= 500) return { level: "error", status: s };
      if (DROP_4XX.has(s)) return { level: "drop", status: s };
      return { level: "warn", status: s };
    }
  }

  // Functions* errors
  if (isFunctionsHttpError(error)) {
    const s = getHttpStatusFromSupabase(error);
    if (s !== undefined) {
      if (s >= 500) return { level: "error", status: s };
      if (DROP_4XX.has(s)) return { level: "drop", status: s };
      return { level: "warn", status: s };
    }
  }

  // PostgrestError → treat as application error
  if (isPostgrestError(error)) {
    return { level: "error" };
  }

  // Generic HTTP classification
  const s = getHttpStatus(error);
  if (s !== undefined) {
    if (s >= 500) return { level: "error", status: s };
    if (DROP_4XX.has(s)) return { level: "drop", status: s };
    return { level: "warn", status: s };
  }

  // Network/offline
  if (isOfflineOrNetwork(error)) return { level: "info" };

  // Fallback
  return { level: "warn" };
}
